import {
  CATEGORIES_BASE,
  POLICIES_BASE,
  TEMPLATES_BASE,
  adsAssistantClientFetch,
  type AdsAssistantFetchResult,
} from "./adsAssistantClientHttp";
import type {
  AdsAssistantCategoryRow,
  AdsAssistantPolicyRow,
  AdsAssistantTemplateRow,
} from "./adsAssistantClientTypes";

export async function listAdsCategories(): Promise<AdsAssistantFetchResult<{ ok: true; categories: AdsAssistantCategoryRow[] }>> {
  return adsAssistantClientFetch(CATEGORIES_BASE, { method: "GET" });
}

export async function createAdsCategory(body: {
  slug: string;
  label: string;
  description?: string;
  sort_order?: number;
}): Promise<AdsAssistantFetchResult<{ ok: true; category: AdsAssistantCategoryRow }>> {
  return adsAssistantClientFetch(CATEGORIES_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateAdsCategory(
  id: string,
  patch: Partial<Pick<AdsAssistantCategoryRow, "slug" | "label" | "description" | "sort_order">>
): Promise<AdsAssistantFetchResult<{ ok: true; category: AdsAssistantCategoryRow }>> {
  return adsAssistantClientFetch(`${CATEGORIES_BASE}?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteAdsCategory(id: string): Promise<AdsAssistantFetchResult<{ ok: true; id: string }>> {
  return adsAssistantClientFetch(`${CATEGORIES_BASE}?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function listAdsTemplates(
  opts?: { includeSignoffProgress?: boolean; includeArchetypeTags?: boolean }
): Promise<AdsAssistantFetchResult<{ ok: true; templates: AdsAssistantTemplateRow[] }>> {
  const includes: string[] = [];
  if (opts?.includeSignoffProgress) includes.push("signoff_progress");
  if (opts?.includeArchetypeTags) includes.push("archetype_tags");
  const q = includes.length ? `?include=${includes.join(",")}` : "";
  return adsAssistantClientFetch(`${TEMPLATES_BASE}${q}`, { method: "GET" });
}

export async function createAdsTemplate(body: {
  name: string;
  category_id?: string | null;
  description?: string;
  body_markdown?: string;
  tags?: string[];
  slots_schema?: unknown[];
  /** Curated illustration posts (≤5); sets template_example_guard; does not change post.template_id. */
  example_social_post_ids?: string[];
  /** Slice 19e — trainer-written exemplar captions (≤5); counted toward sign-off alongside example posts + template_id matches. */
  custom_exemplar_texts?: string[];
  /** Slice 19c — archetype tag ids (≤8); persisted into ads_assistant_template_tag_links after insert. */
  archetype_tag_ids?: string[];
  sort_order?: number;
  is_active?: boolean;
}): Promise<AdsAssistantFetchResult<{ ok: true; template: AdsAssistantTemplateRow }>> {
  return adsAssistantClientFetch(TEMPLATES_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type UpdateAdsTemplatePatch = Partial<
  Pick<
    AdsAssistantTemplateRow,
    | "name"
    | "category_id"
    | "description"
    | "body_markdown"
    | "tags"
    | "sort_order"
    | "is_active"
    | "slots_schema"
    | "trainer_pattern_signed_off"
    | "example_social_post_ids"
    | "custom_exemplar_texts"
  >
> & {
  /** Slice 19b — bypass the org exemplar threshold when signing off (trainer+ only; server re-checks the role). */
  force?: boolean;
  /** Slice 19b — required when `force` is true; persisted to `trainer_pattern_signed_off_forced_reason`. */
  signoff_reason?: string;
  /** Slice 19c — replaces the template's archetype tag set (≤8 archetype-kind ids). */
  archetype_tag_ids?: string[];
};

export async function updateAdsTemplate(
  id: string,
  patch: UpdateAdsTemplatePatch
): Promise<AdsAssistantFetchResult<{ ok: true; template: AdsAssistantTemplateRow }>> {
  return adsAssistantClientFetch(`${TEMPLATES_BASE}?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteAdsTemplate(id: string): Promise<AdsAssistantFetchResult<{ ok: true; id: string }>> {
  return adsAssistantClientFetch(`${TEMPLATES_BASE}?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function listAdsPolicies(activeOnly?: boolean): Promise<AdsAssistantFetchResult<{ ok: true; policies: AdsAssistantPolicyRow[] }>> {
  const q = activeOnly ? "?active_only=true" : "";
  return adsAssistantClientFetch(`${POLICIES_BASE}${q}`, { method: "GET" });
}

export async function createAdsPolicy(body: {
  title: string;
  body_markdown?: string;
  detection_patterns?: { kind: string; value: string; label?: string }[];
  severity?: "info" | "warning" | "critical";
  sort_order?: number;
  is_active?: boolean;
}): Promise<AdsAssistantFetchResult<{ ok: true; policy: AdsAssistantPolicyRow }>> {
  return adsAssistantClientFetch(POLICIES_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateAdsPolicy(
  id: string,
  patch: Partial<
    Pick<AdsAssistantPolicyRow, "title" | "body_markdown" | "detection_patterns" | "severity" | "sort_order" | "is_active">
  >
): Promise<AdsAssistantFetchResult<{ ok: true; policy: AdsAssistantPolicyRow }>> {
  return adsAssistantClientFetch(`${POLICIES_BASE}?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteAdsPolicy(id: string): Promise<AdsAssistantFetchResult<{ ok: true; id: string }>> {
  return adsAssistantClientFetch(`${POLICIES_BASE}?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}
